import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart3, Map, Zap, Lock, User } from 'lucide-react';
import styles from './Auth.module.css';

const AuthLogin = () => {
  const { login, changePassword } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [session, setSession] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(username, password);
      if (!result.success) {
        if (result.error === 'PASSWORD_CHANGE_REQUIRED') {
          setPasswordChangeRequired(true);
          setSession(result.session);
        } else {
          setError(result.error || 'Giriş yapılamadı');
        }
      }
    } catch (err) {
      setError('Giriş yapılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Yeni şifreler eşleşmiyor');
      return;
    }

    if (newPassword.length < 8) {
      setError('Şifre en az 8 karakter olmalıdır');
      return;
    }

    setIsLoading(true);

    try {
      const result = await changePassword(newPassword, session);
      if (!result.success) {
        setError(result.error || 'Şifre değiştirilemedi');
      }
    } catch (err) {
      setError('Şifre değiştirilirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* Arka Plan Dekoratif Elementler */}
      <div className={styles.authBgDecor1}></div>
      <div className={styles.authBgDecor2}></div>

      <div className={styles.authInnerContainer}>
        
        {/* SOL PANEL: Bilgilendirme */}
        <div className={styles.authLeft}>
          <div className={styles.authLeftContent}>
            <h1 className={styles.authTitle}>Pronuve</h1>
            <h2 className={styles.authSubtitle}>
              Akıllı su yönetimi ile şehrinizin geleceğini <span className={styles.highlight}>optimize edin.</span>
            </h2>

            <div className={styles.authFeatures}>
              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <BarChart3 size={28} style={{ color: '#67e8f9' }} />
                </div>
                <div className={styles.featureContent}>
                  <h4>Gelişmiş Veri Analizi</h4>
                  <p>Anlık tüketim verileri ve otomatik PDF raporlama.</p>
                </div>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <Map size={28} style={{ color: '#67e8f9' }} />
                </div>
                <div className={styles.featureContent}>
                  <h4>Coğrafi Entegrasyon</h4>
                  <p>Tüm su şebekesini harita üzerinden canlı izleyin.</p>
                </div>
              </div>

              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <Zap size={28} style={{ color: '#67e8f9' }} />
                </div>
                <div className={styles.featureContent}>
                  <h4>Yapay Zeka Destekli Yönetim</h4>
                  <p>Kayıp-kaçak tespiti ve erken uyarı sistemleri.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.authFooter}>
            © 2024 Pronuve Yazılım Teknoloji A.Ş.
          </div>
        </div>

        {/* SAĞ PANEL: Giriş Formu */}
        <div className={styles.authRight}>
          <div className={styles.authFormContainer}>
            <h3 className={styles.formTitle}>Hoş Geldiniz</h3>
            <p className={styles.formSubtitle}>Lütfen belediye yetkili hesabınızla giriş yapın.</p>

            {!passwordChangeRequired ? (
              <form onSubmit={handleSubmit} className={styles.authForm}>
                <div className={styles.formGroup}>
                  <label>Kullanıcı Adı veya E-posta</label>
                  <div className={styles.inputWrapper}>
                    <User className={styles.inputIcon} size={22} />
                    <input 
                      type="text" 
                      placeholder="kullanici@belediye.gov.tr"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.blueLabel}>
                    Şifre (Geçici Şifre)
                  </label>
                  <div className={styles.inputWrapper}>
                    <Lock className={styles.inputIcon} size={22} />
                    <input 
                      type="password" 
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <span className={styles.helpText}>İlk girişte size iletilen geçici şifreyi kullanınız.</span>
                </div>

                {error && (
                  <div className={styles.error}>
                    {error}
                  </div>
                )}

                <button 
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading}
                >
                  {isLoading ? 'Giriş yapılıyor...' : 'Sisteme Giriş Yap'}
                </button>
              </form>
            ) : (
              <form onSubmit={handlePasswordChange} className={styles.authForm}>
                <div className={styles.passwordChangeInfo}>
                  <p>İlk giriş yapıyorsunuz. Lütfen yeni bir şifre belirleyin.</p>
                </div>

                <div className={styles.formGroup}>
                  <label>Yeni Şifre</label>
                  <div className={styles.inputWrapper}>
                    <Lock className={styles.inputIcon} size={22} />
                    <input 
                      type="password" 
                      placeholder="Yeni şifrenizi girin (min. 8 karakter)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={8}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Yeni Şifre (Tekrar)</label>
                  <div className={styles.inputWrapper}>
                    <Lock className={styles.inputIcon} size={22} />
                    <input 
                      type="password" 
                      placeholder="Yeni şifrenizi tekrar girin"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={8}
                    />
                  </div>
                </div>

                {error && (
                  <div className={styles.error}>
                    {error}
                  </div>
                )}

                <button 
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading}
                >
                  {isLoading ? 'Şifre değiştiriliyor...' : 'Şifreyi Değiştir ve Giriş Yap'}
                </button>
              </form>
            )}

            <div className={styles.supportLink}>
              <a href="mailto:info@pronuve.com">info@pronuve.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLogin;
